import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Footer } from '../components/Footer';
import { toast } from 'sonner';
import authService from '../services/authService';
import subscriptionService from '../services/subscriptionService';

export default function LoginPage({ setIsAuthenticated }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await authService.login(formData.email, formData.password);
      setIsAuthenticated(true);

      // Admin users go straight to admin dashboard — no subscription needed
      if (response.roleName === 'Admin') {
        toast.success(`Welcome back, ${response.name}!`);
        navigate('/admin/users');
        return;
      }

      // Check subscription status after successful login
      const subscriptionStatus = await subscriptionService.checkSubscriptionStatus();

      if (subscriptionStatus.status === 'ACTIVE') {
        // User has active subscription - go to dashboard
        toast.success(`Welcome back, ${response.name}!`);
        navigate('/dashboard');
      } else {
        // No active subscription or expired - go to subscription page
        if (subscriptionStatus.status === 'EXPIRED') {
          toast.warning('Your subscription has expired. Please renew to continue.');
        } else {
          toast.info('Please complete your subscription to access the dashboard.');
        }
        navigate('/subscription');
      }
    } catch (error) {
      toast.error(error || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen gradient-hero flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
      <Card className="w-full max-w-md animate-scale-in">
        <CardHeader className="space-y-4">
          <div className="flex justify-center">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_react-analytics/artifacts/ssguidvr_logo.png" 
                alt="GreenInteliX Logo" 
                className="w-12 h-12 object-contain"
              />
            </Link>
          </div>
          <div className="text-center">
            <CardTitle className="text-2xl font-display">Welcome Back</CardTitle>
            <CardDescription>Sign in to your GreenInteliX account</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@company.com"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                required
              />
            </div>
            <div className="flex items-center justify-between">
              <Link
                to="/forgot-password"
                className="text-sm text-primary hover:underline"
              >
                Forgot password?
              </Link>
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Don't have an account? </span>
            <Link to="/register" className="text-primary hover:underline font-medium">
              Sign up
            </Link>
          </div>
          <div className="mt-4 text-center">
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition">
              ← Back to Home
            </Link>
          </div>
        </CardContent>
      </Card>
      </div>
      <Footer />
    </div>
  );
}